import { type TopicIdentityRecord } from "@/shared/domain/topics";
import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";

import { type SessionFile } from "./SessionFile";
import {
  type CreateSessionRequest,
  type SessionFileContribution,
  type SessionRecord,
  type UpdateSessionRequest,
} from "./SessionRecord";

interface MutateOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

interface GetOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

interface ListOptions {
  abortSignal?: AbortSignal;
  pageToken?: string;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

interface CreateSessionOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Org-Id` header (the caller-org header, distinct from
   * the resource-owner header the other session routes take). Required for
   * callers whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_CALLER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  orgId?: string;
}

/**
 * HTTP client for the `/v1/sessions` API. Construct once per
 * `HttpClient`; resolved by feature code through the
 * `DomainServicesProvider` context.
 */
export class SessionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Attach file contributions to an existing session via
   * `POST /v1/sessions/id/<id>/files`, returning the updated `SessionRecord`
   * whose aggregate bounds reflect the added contributions. Re-adding a file
   * already in the session replaces its range.
   */
  public async addFilesToSession(
    sessionId: string,
    files: SessionFileContribution[],
    options?: MutateOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
    );
    // The server upserts re-added files, so a retried request converges.
    const response = await this.#httpClient.post(url, {
      idempotent: true,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      // bigintReplacer keeps ns-epoch range bounds full-precision on the wire.
      body: JSON.stringify({ files }, bigintReplacer),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  /**
   * Create a session via `POST /v1/sessions`. The session starts with no
   * file contributions; attach them in a follow-up {@link addFilesToSession}
   * call.
   * `useCreateSessionFromFiles` chains the two with delete-on-failure
   * rollback.
   */
  public async createSession(
    request: CreateSessionRequest,
    options?: CreateSessionOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl("v1/sessions");
    // Non-idempotent on the server (each POST mints a new session_id), so a
    // transient 502 must not be silently retried into a duplicate session.
    const response = await this.#httpClient.post(url, {
      idempotent: false,
      headers: options?.orgId
        ? robotoHeaders({ orgId: options.orgId })
        : undefined,
      body: JSON.stringify(request),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  public async deleteSession(
    sessionId: string,
    options?: MutateOptions,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.delete(url, {
      idempotent: false,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    await response.throwIfError();
  }

  public async getSessionById(
    sessionId: string,
    options?: GetOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  public async listSessionFiles(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<SessionFile>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<SessionFile>>(
      parseTimestampsAsBigInt,
    );
  }

  public async listSessionTopics(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<TopicIdentityRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/topics`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<TopicIdentityRecord>>();
  }

  public async updateSession(
    sessionId: string,
    updates: UpdateSessionRequest,
    options?: MutateOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.put(url, {
      idempotent: true,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      body: JSON.stringify(updates),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }
}
